<template>
  <SelectRoot
    v-model:open="isOpen"
    :model-value="modelValue"
    :disabled="isDisabled"
    @update:model-value="updateValue"
  >
    <SelectTrigger
      :id="fieldContext?.inputId"
      ref="trigger"
      :class="[
        `design-system-input relative flex w-full items-center gap-2.5 overflow-hidden rounded-lg border px-2.5 py-1.5 text-left text-sm text-current transition`,
        `outline-2 -outline-offset-1 focus-visible:outline`,
        isDisabled && `opacity-50`,
        fieldContext?.validationStatus.value === 'error'
          ? `border-red-300 outline-red-500 dark:border-red-800`
          : `outline-blue-500`,
      ]"
      :aria-invalid="fieldContext?.validationStatus.value === 'error'"
      :aria-describedby="ariaDescribedBy"
      @focus="onFocus"
      @blur="onBlur"
    >
      <SelectValue class="flex grow items-center gap-2.5">
        <template v-if="selectedColor">
          <div
            :style="{ backgroundColor: selectedColor }"
            class="size-4 shrink-0 rounded-full border border-border"
          ></div>

          <span>{{ selectedLabel }}</span>
        </template>
        <span v-else class="text-muted-foreground">Select a color</span>
      </SelectValue>

      <ChevronDownIcon class="size-5 shrink-0 text-muted-foreground" />

      <input
        ref="nativeInput"
        :value="modelValue"
        type="color"
        tabindex="-1"
        class="pointer-events-none absolute inset-0 size-full opacity-0"
        @input="onNativePickerInput"
      />
    </SelectTrigger>

    <SelectPortal>
      <Transition
        enter-active-class="transition-opacity duration-100 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <SelectContent
          :collision-padding="10"
          class="z-30 flex flex-col rounded-md bg-gray-900 py-2 text-white shadow-lg dark:border dark:border-border dark:bg-card"
        >
          <SelectScrollUpButton class="-mt-2 grid h-7 place-items-center">
            <ChevronDownIcon
              class="size-4 rotate-180 text-gray-400 dark:text-muted-foreground"
            />
          </SelectScrollUpButton>

          <SelectViewport>
            <SelectGroup>
              <SelectItem
                v-for="option in selectOptions"
                :key="option.id"
                :value="option.id"
                class="group relative mx-2 flex shrink-0 cursor-pointer select-none scroll-m-2 gap-3 self-stretch rounded-md border-none px-3 py-1 text-left text-sm outline-none transition-all duration-75 data-[highlighted]:bg-zinc-700 dark:data-[highlighted]:bg-muted"
              >
                <div
                  v-if="option.color"
                  :style="{ backgroundColor: option.color }"
                  class="my-0.5 size-4 shrink-0 rounded-full border border-border"
                ></div>
                <EllipsisHorizontalIcon
                  v-else
                  class="my-0.5 size-4 shrink-0 text-gray-400"
                />

                <div class="flex min-w-0 grow flex-col break-words">
                  <SelectItemText
                    class="transition-opacity group-data-[disabled]:opacity-50"
                  >
                    {{ option.label }}
                  </SelectItemText>
                </div>

                <CheckIcon
                  class="my-0.5 size-4 shrink-0 text-blue-400 opacity-0 transition-opacity duration-75 group-data-[state=checked]:opacity-100"
                />
              </SelectItem>
            </SelectGroup>
          </SelectViewport>

          <SelectScrollDownButton class="-mb-2 grid h-7 place-items-center">
            <ChevronDownIcon
              class="size-4 text-gray-400 dark:text-muted-foreground"
            />
          </SelectScrollDownButton>
        </SelectContent>
      </Transition>
    </SelectPortal>
  </SelectRoot>
</template>

<script lang="ts">
interface ColorPreset {
  label: string;
  color: string;
  value?: string;
}

const defaultColorPresets: ColorPreset[] = [
  { label: "Buttondown blue", color: "#0069FF" },
  { label: "Blue", color: "#3B82F6" },
  { label: "Green", color: "#22C55E" },
  { label: "Yellow", color: "#F59E0B" },
  { label: "Red", color: "#EF4444" },
  { label: "Purple", color: "#8B5CF6" },
  { label: "Pink", color: "#EC4899" },
  { label: "Gray", color: "#6B7280" },
];
</script>

<script lang="ts" setup>
import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectItemText,
  SelectPortal,
  SelectRoot,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger,
  SelectValue,
  SelectViewport,
} from "reka-ui";
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";

import CheckIcon from "@/icons/heroicons/check-micro.svg";
import ChevronDownIcon from "@/icons/heroicons/chevron-down-micro.svg";
import EllipsisHorizontalIcon from "@/icons/heroicons/ellipsis-horizontal-micro.svg";
import { useKeybinds } from "@/lib/hotkeys";

import { injectFieldContext } from "./context";

const CUSTOM_SENTINEL_ID = "pick-" + crypto.randomUUID();

const modelValue = defineModel<string>({ default: "" });

const nativeInput = useTemplateRef("nativeInput");
const trigger = useTemplateRef("trigger");
const isOpen = ref(false);

const props = withDefaults(
  defineProps<{
    presets?: ColorPreset[];
    disabled?: boolean;
    autofocus?: boolean;
    onFocus?: (event: FocusEvent) => void;
    onBlur?: (event: FocusEvent) => void;
  }>(),
  {
    presets: () => defaultColorPresets,
  }
);

const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

const lastPicked = ref<string>();
watch(
  () => modelValue.value,
  (value) => {
    if (
      value &&
      !props.presets.some((item) => (item.value ?? item.color) === value)
    ) {
      lastPicked.value = value;
    }
  },
  { immediate: true }
);

const selectOptions = computed(() => {
  const options: Array<{
    id: string;
    label: string;
    color?: string;
  }> = [];

  for (const preset of props.presets) {
    options.push({
      id: preset.value ?? preset.color,
      label: preset.label,
      color: preset.color,
    });
  }

  if (lastPicked.value) {
    options.push({
      id: lastPicked.value,
      label: `Most recently used color (${lastPicked.value})`,
      color: lastPicked.value,
    });
  }

  options.push({
    id: CUSTOM_SENTINEL_ID,
    label: "Custom color",
  });

  return options;
});

const selectedColor = computed(() => {
  if (!modelValue.value) {
    return null;
  }

  const option = selectOptions.value.find((opt) => opt.id === modelValue.value);
  return option?.color ?? null;
});

const selectedLabel = computed(() => {
  if (!modelValue.value) return null;

  const option = selectOptions.value.find((opt) => opt.id === modelValue.value);
  return option?.label ?? null;
});

const updateValue = (next: string) => {
  if (next === CUSTOM_SENTINEL_ID) {
    nativeInput.value?.click();
    return;
  }

  modelValue.value = next;
};

const onNativePickerInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  modelValue.value = target.value;
};

useKeybinds(() => {
  if (!isOpen.value) {
    return;
  }

  return {
    Escape: {
      name: `Close color picker`,
      group: `Color picker`,
      run: (ev) => {
        if (ev.defaultPrevented) {
          return;
        }

        isOpen.value = false;
        return true;
      },
    },
  };
});

onMounted(() => {
  if (props.autofocus) {
    trigger.value?.$el?.focus({ preventScroll: true });
  }
});

defineExpose({
  input: computed(() => {
    return trigger.value?.$el as HTMLButtonElement | null | undefined;
  }),
});
</script>
